import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { listMyAds, listAllAds, listAdPackages, checkoutAd, resolveFileUrl } from '../lib/api';
import { Megaphone, ExternalLink, Eye, Monitor, Smartphone, Loader2, CreditCard, CheckCircle2, Play } from 'lucide-react';
import { toast } from 'sonner';

// Inline-only static ad previews (no API / no impression firing)
function PreviewHomeBanner({ ad }) {
  return (
    <div className="relative card-shell overflow-hidden">
      <div className="grid sm:grid-cols-[1fr_1.3fr] items-center">
        <div className="p-5">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#ffcf52]"><Megaphone className="w-3.5 h-3.5" /> Sponsored</div>
          <div className="font-display text-[22px] md:text-[28px] leading-tight mt-2">{ad.headline || 'Your headline'}</div>
          {ad.body && <p className="text-white/70 text-[13px] mt-2 leading-relaxed line-clamp-3">{ad.body}</p>}
          {ad.link_url && <div className="mt-3 inline-flex items-center gap-1 text-[13px] text-[#22d3ee]">Learn more <ExternalLink className="w-3.5 h-3.5" /></div>}
        </div>
        <div className="relative h-40 sm:h-full min-h-[160px]">
          <img src={resolveFileUrl(ad.media_url) || 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=1200'} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #111114, rgba(17,17,20,0) 40%)' }} />
        </div>
      </div>
    </div>
  );
}
function PreviewPromoted({ ad }) {
  return (
    <div className="w-[260px]">
      <div className="relative thumb" style={{ background: '#141418' }}>
        <img src={resolveFileUrl(ad.media_url) || 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=900'} alt="" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.8), rgba(0,0,0,0) 55%)' }} />
        <div className="absolute top-2 left-2 text-[10.5px] uppercase tracking-wider bg-black/65 border border-white/10 rounded-full px-2 py-0.5 flex items-center gap-1 text-[#ffcf52]"><Megaphone className="w-3 h-3" /> Promoted</div>
        <div className="absolute bottom-2 left-2 right-2 text-white">
          <div className="text-[14px] font-semibold line-clamp-2">{ad.headline || 'Your headline'}</div>
          {ad.body && <div className="text-[12px] text-white/70 line-clamp-1 mt-0.5">{ad.body}</div>}
        </div>
      </div>
      <div className="mt-2 text-[13px] text-white/70">Sponsored • tap to watch</div>
    </div>
  );
}
function PreviewPreroll({ ad }) {
  return (
    <div className="relative w-full max-w-[640px] aspect-video rounded-xl overflow-hidden bg-black">
      <img src={resolveFileUrl(ad.media_url) || 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600'} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85), rgba(0,0,0,0.25) 60%)' }} />
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-black/60 border border-white/10 rounded-full px-2.5 py-1">
        <Megaphone className="w-3 h-3 text-[#ffcf52]" /> Ad • Pre-roll
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6 text-white">
        <div className="font-display text-[26px] md:text-[34px] leading-tight">{ad.headline || 'Your headline'}</div>
        {ad.body && <p className="text-white/85 mt-1 text-[14px] max-w-[620px]">{ad.body}</p>}
        {ad.link_url && <div className="mt-3 btn-primary inline-flex text-[13px]"><Play className="w-3.5 h-3.5" /> Visit site</div>}
      </div>
      <div className="absolute top-3 right-3 text-[12px] bg-black/60 border border-white/10 rounded-full px-3 py-1 text-white/85">Skip in 5s</div>
    </div>
  );
}
function PreviewSponsor({ ad }) {
  return (
    <div className="card-shell p-4 flex items-center gap-4 max-w-[640px]">
      <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/5 border border-white/10 grid place-items-center">
        {ad.media_url ? <img src={resolveFileUrl(ad.media_url)} alt="" className="w-full h-full object-cover" /> : <Megaphone className="w-6 h-6 text-white/70" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#ffcf52]"><Megaphone className="w-3 h-3" /> Sponsor</div>
        <div className="font-semibold text-[15px] mt-0.5">{ad.headline || 'Your headline'}</div>
        {ad.body && <div className="text-[13px] text-white/65 line-clamp-1">{ad.body}</div>}
      </div>
      {ad.link_url && <div className="text-[13px] text-[#22d3ee] flex items-center gap-1 shrink-0">Visit <ExternalLink className="w-3.5 h-3.5" /></div>}
    </div>
  );
}
function PreviewExternal({ ad }) {
  const srcDoc = `<!doctype html><html><head><base target="_blank"><style>html,body{margin:0;background:#0a0a0c;color:#fff;font-family:Inter,system-ui;display:grid;place-items:center;height:100%}</style></head><body>${ad.external_html || '<div style="padding:40px;color:#888">No external HTML provided</div>'}</body></html>`;
  return (
    <iframe title="ext-preview" srcDoc={srcDoc} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      className="w-full max-w-[640px] h-[180px] rounded-xl border border-white/10 bg-black" />
  );
}

const PREVIEW_MAP = {
  home_banner: PreviewHomeBanner,
  preroll: PreviewPreroll,
  row_promoted: PreviewPromoted,
  channel_sponsor: PreviewSponsor,
  external_banner: PreviewExternal,
};
const PLACEMENT_LABEL = {
  home_banner: 'Home Banner',
  preroll: 'Pre-roll video ad',
  row_promoted: 'Promoted Row card',
  channel_sponsor: 'Channel Sponsor strip',
  external_banner: 'External / AdSense slot',
};

export default function AdMarketplace() {
  const { user } = useApp();
  const [myAds, setMyAds] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedAd, setSelectedAd] = useState(null);
  const [viewport, setViewport] = useState('desktop');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ads, pkgs] = await Promise.all([
          (user?.role === 'owner' ? listAllAds() : listMyAds()),
          listAdPackages(),
        ]);
        setMyAds(ads); setPackages(pkgs);
        if (ads.length) setSelectedAd(ads[0]);
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, [user]);

  const relevantPackages = useMemo(() => {
    if (!selectedAd) return packages;
    return packages.filter(p => p.placement === selectedAd.placement || user?.role === 'owner');
  }, [packages, selectedAd, user]);

  const buy = async (pkg) => {
    if (!selectedAd) return toast.error('Select an ad first');
    setBusy(pkg.id);
    try {
      const { url } = await checkoutAd(pkg.id, selectedAd.id);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  const Preview = selectedAd ? (PREVIEW_MAP[selectedAd.placement] || PreviewHomeBanner) : null;

  return (
    <div data-testid="ad-marketplace">
      <div className="flex items-center gap-2 text-[#ffcf52]"><Megaphone className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Ad Marketplace</span></div>
      <h1 className="font-display text-[32px] md:text-[40px] mt-2 leading-none">Preview & buy placements</h1>
      <p className="text-white/60 text-[14px] mt-1">See exactly how your ad looks on VidCreForge, then buy a flat-rate placement block. Secure checkout by Stripe.</p>

      {loading ? <div className="flex items-center gap-2 text-white/60 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div> : (
        <div className="grid lg:grid-cols-[320px_1fr] gap-6 mt-8">
          <aside className="card-shell p-4 h-max">
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Your ads</div>
            {myAds.length === 0 ? (
              <div className="text-[13px] text-white/60">You don&apos;t have any ads yet. Create one on the Ads page first.</div>
            ) : (
              <ul className="space-y-1 max-h-[480px] overflow-y-auto">
                {myAds.map(ad => (
                  <li key={ad.id}>
                    <button onClick={() => setSelectedAd(ad)}
                      className={`w-full text-left p-3 rounded-lg transition ${selectedAd?.id === ad.id ? 'bg-white/10' : 'hover:bg-white/[0.04]'}`}
                      data-testid={`ad-item-${ad.id}`}>
                      <div className="font-semibold text-[13.5px] line-clamp-1">{ad.headline || 'Untitled ad'}</div>
                      <div className="text-[11.5px] text-white/55 mt-0.5 flex items-center gap-2">
                        <span className="chip px-1.5 py-0 text-[10px]">{PLACEMENT_LABEL[ad.placement] || ad.placement}</span>
                        {ad.active ? <span className="text-[#22d3ee]">live</span> : <span className="text-white/45">draft</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="space-y-6">
            {selectedAd && Preview && (
              <>
                <div className="card-shell p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[12px] uppercase tracking-wider text-white/45">Live preview</div>
                      <h3 className="font-display text-[22px] mt-1">{PLACEMENT_LABEL[selectedAd.placement] || selectedAd.placement}</h3>
                    </div>
                    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                      <button onClick={() => setViewport('desktop')} className={`px-3 py-1.5 text-[12.5px] flex items-center gap-1.5 ${viewport === 'desktop' ? 'bg-white/10' : 'text-white/60 hover:bg-white/[0.04]'}`} data-testid="vp-desktop"><Monitor className="w-3.5 h-3.5" /> Desktop</button>
                      <button onClick={() => setViewport('mobile')} className={`px-3 py-1.5 text-[12.5px] flex items-center gap-1.5 ${viewport === 'mobile' ? 'bg-white/10' : 'text-white/60 hover:bg-white/[0.04]'}`} data-testid="vp-mobile"><Smartphone className="w-3.5 h-3.5" /> Mobile</button>
                    </div>
                  </div>
                  <div className="mt-5 p-5 rounded-xl" style={{ background: '#070709', border: '1px dashed rgba(255,255,255,0.08)' }}>
                    <div style={{ maxWidth: viewport === 'mobile' ? 380 : '100%', margin: viewport === 'mobile' ? '0 auto' : undefined }} data-testid="preview-canvas">
                      <Preview ad={selectedAd} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-4 text-[12px] text-white/55">
                    <div className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {selectedAd.impressions || 0} impressions</div>
                    <div>{selectedAd.clicks || 0} clicks</div>
                    <div>${Number(selectedAd.spent || 0).toFixed(2)} spent</div>
                    {selectedAd.ends_at && <div>Ends {new Date(selectedAd.ends_at).toLocaleDateString()}</div>}
                  </div>
                </div>

                <div className="card-shell p-5">
                  <div className="text-[12px] uppercase tracking-wider text-white/45">Buy a placement block</div>
                  <h3 className="font-display text-[22px] mt-1">Flat-rate packages</h3>
                  <p className="text-white/55 text-[13px] mt-1">No bidding, no CPM surprises — pay once, run live for the whole period.</p>

                  {relevantPackages.length === 0 ? (
                    <div className="text-[13px] text-white/55 mt-4">No packages match this ad&apos;s placement.</div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3 mt-5">
                      {relevantPackages.map(p => (
                        <div key={p.id} className="card-shell p-4 flex flex-col">
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-[14.5px]">{p.name}</div>
                            <span className="chip text-[11px]">{p.days}d</span>
                          </div>
                          <div className="mt-2 flex items-baseline gap-1">
                            <span className="font-display text-[28px]">${p.price.toFixed(0)}</span>
                            <span className="text-white/50 text-[12px]">one-time</span>
                          </div>
                          <div className="text-[12.5px] text-white/55 mt-1">Runs for {p.days} day{p.days > 1 ? 's' : ''} from purchase</div>
                          <button onClick={() => buy(p)} disabled={busy === p.id} className="btn-primary mt-4 w-full disabled:opacity-50" data-testid={`buy-${p.id}`}>
                            {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Buy now</>}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2 text-[11.5px] text-white/45">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#22d3ee]" /> Secure checkout by Stripe — you&apos;ll be redirected and returned when complete.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
